import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Enables content protection on public routes:
 *  - Toggles a body class that disables text selection + image drag via CSS
 *  - Blocks contextmenu (right-click) + Ctrl+U / Ctrl+S / Ctrl+P
 *  - Disables native image drag start
 *
 * Skips /admin, /login, /dashboard so admins can copy IDs, keys, etc.
 * Form inputs and any element with class `select-ok` remain fully interactive.
 */
export const useContentProtection = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isProtected =
      !path.startsWith('/admin') &&
      !path.startsWith('/login') &&
      !path.startsWith('/dashboard');

    if (!isProtected) {
      document.body.classList.remove('content-protected');
      return;
    }

    document.body.classList.add('content-protected');

    const onContextMenu = (e) => {
      // Allow context menu on form inputs so users can paste.
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
    };
    const onDragStart = (e) => {
      if (e.target?.tagName === 'IMG') e.preventDefault();
    };
    const onKeyDown = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const k = e.key.toLowerCase();
      // Block view-source (Ctrl+U), save (Ctrl+S), print (Ctrl+P).
      // Copy/cut/paste (c/x/v) and select-all (a) are NOT blocked in inputs.
      const tag = e.target?.tagName;
      const inForm = tag === 'INPUT' || tag === 'TEXTAREA';
      if (['u', 's', 'p'].includes(k)) {
        e.preventDefault();
      } else if (['c', 'a'].includes(k) && !inForm) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('dragstart', onDragStart);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [location.pathname]);
};
